import { SelectedMasterKeyService, MasterKey, KeywayService } from '@overtur-monorepo/keying-hierarchy'
import { Keyway } from '@overtur-monorepo/keying-shared'
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { fadeIn } from '../../../animations/fadeIn'
import { slideInDown } from '../../../animations/slideInDown'
import { Book, Structure } from '../../../models/dev.test.models'
import { FormatDataService } from '../../../services/format.data.service'
import { KeyBoardService } from '../../../services/keyboard.service'
import { KeyingBookService } from '../../../services/keying.book.service'
import { KeyingDataSetsService, StructureDataSets } from '../../../services/keying.data.sets.service'
import { KeyingLibraryService } from '../../../services/keying.library.service'
import { LoggingService } from '../../../services/logging.service'
import { KeywayType } from '@overtur-monorepo/keying-shared'
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component'
import { DialogService } from '../../../services/dialog.service'

@Component({
    selector: 'app-key-bitting-matrix',
    templateUrl: './key-bitting-matrix.component.html',
    styleUrls: ['./key-bitting-matrix.component.scss'],
    animations: [fadeIn, slideInDown],
    standalone: false
})
export class KeyBittingMatrixComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()

  @Input() keySystem
  @Input() selectedMasterKey
  @Input() matrix
  @Input() structure: Structure = null

  @Output() matrixUpdates = new EventEmitter<any>()
  @Output() disableWriteBook = new EventEmitter<any>()

  columns: number = 0
  inputs

  selectedBook: Book
  dialogRef

  bittingDataSets
  validateUserInputs
  validateKbaColumnNotEmpty
  disableWribookbtn
  emptyValuesKba = []

  keySectionsList: Keyway[] = []
  keySections: Keyway[] = []
  defaultKeySection: Keyway
  selectedBookKeyway: Keyway

  isKeySectionsLoaded = false

  constructor(
    private keyingLibraryService: KeyingLibraryService,
    public keyingDataSetsService: KeyingDataSetsService,
    public keyingBookService: KeyingBookService,
    private formatDataService: FormatDataService,
    private keyboardService: KeyBoardService,
    private loggingService: LoggingService,
    private selectedMasterKeyService: SelectedMasterKeyService,
    private keywayService: KeywayService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    //subscribe to changes
    this.onChanges()
    this.keyingLibraryService.selectedBook$.pipe(takeUntil(this.destroy$)).subscribe((book: Book) => {
      this.selectedBook = book
      if (book) {
        this.loadKeySections()
        this.emitBookKeyway()
      }
    })

    this.keyingDataSetsService.structureDataSets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((structureDataSets: StructureDataSets) => {
        if (!structureDataSets) {
          return
        }

        this.keyingBookService.bittingDataSets.pipe(takeUntil(this.destroy$)).subscribe((bittingData) => {
          if (!bittingData) {
            return
          }

          this.bittingDataSets = bittingData
          if (!this.selectedBook) {
            this.matrix = this.bittingDataSets.matrix
          } else if (this.selectedBook) {
            this.matrix = this.selectedBook
          }
          this.columns = this.matrix.masterKeyBitting.length

          this.loggingService.log('Matrix', this.matrix)
        })
      })

    // Subscribe to Selected Book
    this.keyingLibraryService.selectedBook$.pipe(takeUntil(this.destroy$)).subscribe((book: Book) => {
      this.selectedBook = book
      if (!this.selectedBook && this.bittingDataSets) {
        this.matrix = this.bittingDataSets.matrix
      } else if (this.selectedBook) {
        this.matrix = this.selectedBook
      }

      // TODO: Matrix data does not include keyway
      // this.setKeywayById(this.matrix.keywayId)
    })

    this.keyboardService.keyBoard.subscribe((res) => {
      this.move(res)
    })

    this.keywayService.keyways$.pipe(takeUntil(this.destroy$)).subscribe((keyways: Keyway[]) => {
      this.setKeyWays(keyways)
    })

    this.loadKeySections()
  }

  private onChanges(): void {
    this.onKeySelectionChange()
  }

  private onKeySelectionChange() {
    this.selectedMasterKeyService.selectedMasterKey$
      .pipe(takeUntil(this.destroy$))
      .subscribe((masterKey: MasterKey) => {
        const isMasterKeyChanged = masterKey.id !== this.selectedMasterKey.id
        this.selectedMasterKey = masterKey
        if (this.selectedMasterKey && isMasterKeyChanged) {
          this.loadKeySections(true)
        }
      })
  }

  loadKeySections(isMasterKeyChanged = false) {
    if (!isMasterKeyChanged && this.isKeySectionsLoaded) {
      return
    }

    this.isKeySectionsLoaded = true
    const keywayFamilyId = this.structure.keywayFamilyId
    const keySectionId = this.selectedMasterKey.keywayId
    if (keySectionId) {
      this.keywayService
        .getKeyways(keywayFamilyId, keySectionId)
        .pipe(takeUntil(this.destroy$))
        .subscribe((keyway) => {
          this.keySections = []
          this.keySections = keyway?.childKeyways
          this.keySections.push(keyway)
          this.keySections = this.keySections.filter((cks) => cks.keywayTypeId !== KeywayType.Plug)
          this.keySections.sort((a, b) => (a.name > b.name ? 1 : -1))
          this.defaultKeySection = this.keySections.find((kwy) => kwy.id === keySectionId)
          if (this.selectedBook) {
            const keySection = this.keySections.find((kwy) => kwy.id === this.selectedBook.keywayId)
            this.keyingLibraryService.emitKeySection(keySection)
          } else {
            this.keyingLibraryService.emitKeySection(this.defaultKeySection)
          }
        })
    }
  }

  private setKeyWays(keyways: Keyway[]) {
    this.keySectionsList = []
    this.keySectionsList = keyways
    this.emitBookKeyway()
  }

  emitKeySection(keySection) {
    this.defaultKeySection = keySection
    this.keyingLibraryService.emitKeySection(this.defaultKeySection)
    this.update()
  }

  private emitBookKeyway(): void {
    if (this.selectedBook) {
      this.selectedBookKeyway = this.keySectionsList?.find((kwy) => kwy.id === this.selectedBook.keywayId)
    }
  }

  setInputs(inputs) {
    this.inputs = inputs
  }

  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  checkIfDuplicateExists(arr) {
    return new Set(arr).size !== arr.length
  }

  checkIfKbaColumnEmpty() {
    let indexOfMask = []
    this.matrix.mask.forEach((x, i) => {
      if (x === '*') {
        indexOfMask.push(i)
      }
    })
    let ref = false
    indexOfMask.forEach((value) => {
      ref = this.checkForKBAEmptyColumn(value)
    })
    if (ref && !this.dialogRef) {
      if (this.emptyValuesKba.length === this.matrix.keyBitArray.length) {
        let data = {
          title: 'Mask Error',
          message: `Chamber cannot be progressed with all digits removed`,
          cancel: 'Cancel',
          confirm: 'Ok',
          showCancel: true,
          reasonRequired: false,
        }

        this.dialogRef = this.dialogService.open(ConfirmDialogComponent, data, false)

        this.dialogRef.afterClosed().subscribe(() => {
          this.dialogRef = null
        })

        this.validateKbaColumnNotEmpty = true
      } else {
        this.validateKbaColumnNotEmpty = false
      }
    }
  }

  checkForKBAEmptyColumn(indexOfMaskValue) {
    this.emptyValuesKba = []
    let openDialog = false
    this.matrix.keyBitArray.forEach((kba) => {
      kba.forEach((value, i) => {
        if (i === indexOfMaskValue && value === '') {
          openDialog = true
          this.emptyValuesKba.push(value)
        }
      })
    })

    return openDialog
  }

  update() {
    if (
      !this.matrix.mask.some((bit) => bit === null || bit === '') &&
      !this.matrix.sequenceOfProgression.some(
        (bit) => bit === null || bit === '' || this.checkIfDuplicateExists(this.matrix.sequenceOfProgression)
      )
    ) {
      this.validateUserInputs = false
    } else {
      this.validateUserInputs = true
    }

    if (this.matrix) {
      let deepCopy = JSON.parse(JSON.stringify(this.matrix.keyBitArray))
      deepCopy = this.formatDataService.replaceWith(deepCopy, 'S')

      let request = {
        mask: this.matrix.mask.join(''),
        sequenceOfProgression: this.matrix.sequenceOfProgression.join(''),
        masterKeyBitting: this.matrix.masterKeyBitting.join(''),
        keyBitArray: deepCopy.map((b) => (b = b.join(''))),
        keywayId: this.defaultKeySection.id,
        keywayTypeId: this.defaultKeySection.keywayTypeId,
      }

      if (this.validateUserInputs || this.validateKbaColumnNotEmpty) {
        this.disableWribookbtn = true
      } else {
        this.disableWribookbtn = false
      }
      this.matrixUpdates.emit(request)
      this.disableWriteBook.emit(this.disableWribookbtn)
    }
  }

  selectBook(book) {
    this.keyingLibraryService.selectBook(book)
  }

  move(object) {
    const inputToArray = this.inputs.toArray()
    let index = inputToArray.findIndex((x) => x.element == object.element)
    switch (object.action) {
      case 'UP':
        index -= this.columns
        break
      case 'DOWN':
        index += this.columns
        break
      case 'LEFT':
        index--
        break
      case 'RIGTH':
        index++
        break
      case 'RIGTH':
        index++
        break
    }

    if (index >= 0 && index < this.inputs.length) inputToArray[index].element.nativeElement.focus()
  }
}
